/**
 * @file user.model.ts
 * @description User-related interfaces for authentication and profile data.
 * Maps to the backend's /users/get response (UserToken) and Cognito auth shapes.
 */

/**
 * Authenticated user profile as returned by GET /users/get.
 * This is the primary user state object used throughout the app.
 */
export interface UserToken {
  /** User's email address (from Cognito) */
  email: string;
  /** User's display name */
  displayName: string;
  /** User role (e.g., 'user', 'admin') */
  role: string;
  /** List of subscription IDs the user has access to */
  subscriptions: string[];
  /** Currently active subscription ID */
  currentSubscription: string;
  /** Total token balance (delivered as `available_credits`). */
  availableCredits: number;
  /** Tokens already consumed (lifetime). */
  consumedCredits: number;
  /** Tokens currently held by in-flight runs; spendable = availableCredits − reservedCredits. */
  reservedCredits?: number;
  /** Monthly Credits: tokens granted this calendar month by the active pack (0 if none). */
  monthlyAllowance: number;
  /** Monthly Credits: tokens consumed in the current calendar month. */
  consumedThisPeriod: number;
  /** Monthly Credits: ISO datetime when the allowance resets (null if no subscription). */
  periodResetDate: string | null;
  /** Personal wallet balance in integer credits (C7 `credit_balance`; spendable under any plan). */
  creditBalance: number;
  /** Number of unread notifications (for bell badge) */
  unreadNotificationsCount: number;
  /** Full details of subscriptions the user has access to (owner, status, price tier, etc). */
  allowedSubscriptions?: UserAllowedSubscription[];
  /** Last accepted version of the General Terms of Service */
  acceptedVersionOfGeneralTerms?: string;
  /** Last accepted version of the Run Analysis Terms */
  acceptedVersionOfRunAnalysisTerms?: string;
  /**
   * True when this email's one-time Free benefit was consumed by a previous
   * (deleted) account (backend `free_plan_consumed`, 2026-07-23 spec). The
   * historical fact — stays true even after a paid upgrade. Never inferred
   * from a zero balance.
   */
  freePlanConsumed?: boolean;
  /**
   * Backend `restriction_reason`: `'FREE_PLAN_ALREADY_USED'` while the reuse
   * restriction is in force (no entitled paid plan lifting it), else null.
   */
  restrictionReason?: string | null;
}

/**
 * Allowed subscription entry — represents a user's access to a specific subscription.
 */
export interface UserAllowedSubscription {
  /** Subscription ID */
  subscriptionId: string;
  /** User's role within this subscription */
  role: string;
  /** Subscription owner email */
  owner: string;
  /** Subscription status */
  status: string;
  /** Billing cycle (e.g., 'monthly', 'yearly') */
  billingCycleInterval: string;
  /** Next billing date (ISO string) */
  nextBilledAt: string | null;
  /** Price tier name */
  priceName: string | null;
  /**
   * Human-readable subscription name (from the backend `display_name`
   * field). Preferred over `priceName` for rendering in the UI — falls
   * back to `priceName`, then the subscription id.
   */
  displayName: string | null;
  /** Tokens granted to THIS subscription for the current calendar month (0 = not metered monthly). */
  monthlyAllowance: number;
  /** Tokens debited against THIS subscription in the current calendar month. */
  consumedThisPeriod: number;
  /** ISO datetime the monthly allowance resets (first of next month, UTC); null when no allowance. */
  periodResetDate: string | null;
  /**
   * Tokens THIS plan can spend: its own pool plus the personal wallet
   * (per-subscription pools, 2026-07-21) — never another plan's balance.
   * `undefined` when the backend predates per-plan pools (wallet fallback).
   */
  availableTokens?: number;
  /** Lifetime tokens drained from the same pools; `undefined` when not sent. */
  consumedTokens?: number;
  /**
   * Monthly plan allowance in integer credits (C7). 0 = not metered monthly
   * (Free/synthetic); `undefined` when the backend predates the C7 credit
   * fields — "no data" must never be mistaken for a zero balance, so the
   * resolver falls back to the personal wallet instead.
   */
  monthlyCreditAllowance?: number;
  /** Spendable balance of THIS plan in integer credits (floor; its pool + the personal wallet). `undefined` when not sent. */
  creditsAvailable?: number;
  /** Credits consumed against THIS plan in the current period (ceil). `undefined` when not sent. */
  creditsConsumedThisPeriod?: number;
  /**
   * The plan's OWN pool balance in integer credits (floor) — personal wallet
   * EXCLUDED (2026-08-04 meter-headline split: a 100-credit pilot must read
   * "100" whatever the wallet holds). `undefined` when the backend predates
   * the field; display falls back to `creditsAvailable`.
   */
  planCreditsAvailable?: number;
  /** Lifetime credits drained from the plan's own pool (ceil); wallet excluded. `undefined` when not sent. */
  planCreditsConsumed?: number;
  /**
   * Tokens held by THIS plan's in-flight analyses (`reserved_tokens`).
   * `availableTokens` is already NET of this hold — never subtract it again.
   * `undefined` when the backend predates reservations.
   */
  reservedTokens?: number;
  /**
   * Integer credits held by THIS plan's in-flight analyses
   * (`credits_reserved`, backend-derived — render verbatim, never convert).
   * `creditsAvailable` is already NET of this hold. `undefined` when not sent.
   */
  creditsReserved?: number;
}

/**
 * Display label for a subscription entry: prefers the backend-provided
 * `displayName`, then the Paddle-style `priceName`, then the raw id. Shared by
 * the header switcher, the account page, and manage-subscriptions so the same
 * subscription can never be named differently across surfaces.
 */
export function subscriptionDisplayLabel(sub: UserAllowedSubscription): string {
  return sub.displayName?.trim() || sub.priceName?.trim() || sub.subscriptionId;
}

/**
 * Plan label shown when the user has no subscription entry at all — a fresh
 * account that has not yet subscribed any plan (Free included; the Free plan
 * is an explicit subscription whose synthetic `free` entry carries its own
 * "Free" display_name). Analyses and References are gated for this state;
 * every entry point routes to /pricing.
 */
export const NO_PLAN_LABEL = 'No plan';

/**
 * Synthetic subscription id of the read-time-only Free plan entry in
 * `GET /users/get → allowed_subscriptions` (emitted once the user subscribes
 * the Free plan). Never persisted server-side. Also the `subscription_id`
 * that Free users' analyses are billed against in `/reports/start-analysis`.
 */
export const FREE_SUBSCRIPTION_ID = 'free';

/**
 * Usage figures for the currently-active subscription, resolved for display.
 * `source` says which accounting backs the numbers:
 *  - `monthly` — the active subscription carries a positive monthly allowance
 *    (paid plan); consumed/reset are that subscription's current-month figures.
 *  - `wallet` — the active subscription is not metered monthly (Free/PAYG or
 *    allowance not yet set); figures fall back to the user-level token wallet,
 *    where the bar's 100% is owned balance + lifetime consumption.
 * `reserved` is always the user-level wallet hold (in-flight runs) — reservations
 * are not tracked per subscription (2026-07-01 usage-meter spec, decision 6).
 */
export interface ActiveSubscriptionUsage {
  allowance: number;
  consumed: number;
  reserved: number;
  /**
   * Gross spendable token balance of the ACTIVE plan: the entry's own
   * `availableTokens` (its pool + the personal wallet), falling back to the
   * user-level wallet when the backend sends no per-plan figures. This is the
   * single "available tokens" figure every surface shows and gates on —
   * plan balances are never added together.
   */
  available: number;
  resetDate: string | null;
  source: 'monthly' | 'wallet';
  /**
   * Credit figures (backend-authoritative integers, C7). `creditAllowance` is the
   * monthly allowance on paid plans; in wallet mode it is synthesized as
   * available + consumed so "used of allowance" keeps its meaning (mirrors the
   * legacy token-mode wallet bar). The frontend never converts tokens.
   */
  creditAllowance: number;
  creditsAvailable: number;
  creditsConsumed: number;
  /**
   * Meter-headline figure: the ACTIVE plan's own pool in integer credits,
   * personal wallet EXCLUDED (2026-08-04). Falls back to `creditsAvailable`
   * when the backend predates the plan-scoped fields. Display-only — every
   * pre-flight gate keeps using `creditsAvailable` (spendable, pool + wallet)
   * so the client mirrors the server-side financial authorization.
   */
  planCreditsAvailable: number;
  /**
   * Personal-wallet credits ALSO spendable under the active plan, derived as
   * `creditsAvailable - planCreditsAvailable` — the additive "plus N credits
   * in your personal wallet" note. 0 when the wallet is empty, when the
   * active entry IS the wallet, or when the backend sends no plan-scoped
   * figures (the headline then already shows the spendable figure).
   */
  walletCreditsAvailable: number;
  /**
   * Integer credits held by the ACTIVE plan's in-flight analyses
   * (backend-derived, rendered verbatim). Display-only: `creditsAvailable`
   * is already NET of this hold, so it never enters the meter math.
   * 0 when the backend sends no figure.
   */
  creditsReserved: number;
}
